import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../utils/api';
import { User, Award, Star, Clock, Flame, Medal, ChevronRight, Zap, Heart, MessageSquare, CheckCircle, Edit3, Trash2, Calendar, Users as UsersIcon, MapPin } from 'lucide-react';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { API } from '../utils/api';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import CommunityImpactSections from '../components/community/CommunityImpactSections';

const COMM = `${API}/community`;
const TEAL = '#47b8a3';
const TEAL_LIGHT = '#EDF8F6';
const ORANGE_LIGHT = '#FEF7ED';

export default function CommunityProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useCommunityAuth();
  const isAdmin = user?.role === 'COMMUNITY_ADMIN';
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [myEvents, setMyEvents] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [myActivity, setMyActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialTab = searchParams.get('tab') || 'myevents';
  const [activeTab, setActiveTab] = useState(initialTab);
  // Sub-filter shared between the (legacy) MAD list and the new Events list;
  // 'active' here means upcoming/in-progress, 'completed' means past/attended.
  const [madSubFilter, setMadSubFilter] = useState('active');

  // Weekly glance
  const [weekDays, setWeekDays] = useState([]);

  useEffect(() => { fetchData(); initWeek(); }, []);

  const initWeek = () => {
    const today = new Date();
    const dow = today.getDay();
    const monOff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(today);
    mon.setDate(today.getDate() + monOff);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(mon);
      d.setDate(mon.getDate() + i);
      days.push({ dayName: d.toLocaleDateString('en-US', { weekday: 'short' }), dayNum: d.getDate(), isToday: d.toDateString() === today.toDateString() });
    }
    setWeekDays(days);
  };

  const fetchData = async () => {
    try {
      const [evRes, postRes, actRes] = await Promise.all([
        axios.get(`${COMM}/events/my`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/posts/my`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/activity/my`, authHeaders).catch(() => ({ data: [] }))
      ]);
      setMyEvents(evRes.data || []);
      setMyPosts(postRes.data || []);
      setMyActivity(actRes.data || []);
    } catch (e) { console.error('Failed to load profile data', e); }
    finally { setLoading(false); }
  };

  const getImpactBadge = (level) => {
    const cfg = { 'Bronze': { icon: Medal, color: '#CD7F32', bg: 'rgba(205,127,50,0.15)' }, 'Silver': { icon: Medal, color: '#A8A8A8', bg: 'rgba(168,168,168,0.15)' }, 'Gold': { icon: Medal, color: '#D4AF37', bg: 'rgba(212,175,55,0.15)' }, 'Platinum': { icon: Star, color: '#9D5CFF', bg: 'rgba(157,92,255,0.15)' }, 'Hero': { icon: Star, color: '#F5A623', bg: 'rgba(245,166,35,0.15)' } }[level] || { icon: Medal, color: '#CD7F32', bg: 'rgba(205,127,50,0.15)' };
    const I = cfg.icon;
    return <div style={{ width: 20, height: 20, borderRadius: 6, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><I size={12} color={cfg.color} /></div>;
  };

  const getActivityIcon = (type) => {
    const icons = { 'post': <Edit3 size={14} color={TEAL} />, 'challenge_join': <Zap size={14} color="#F59E0B" />, 'progress': <CheckCircle size={14} color="#10B981" />, 'like': <Heart size={14} color="#EF4444" />, 'comment': <MessageSquare size={14} color="#6366F1" /> };
    return icons[type] || <Star size={14} color={TEAL} />;
  };

  if (loading || !user) return <div className="loading">Loading...</div>;

  // Partition the user's events for the Active vs Completed sub-filters.
  // Active/Upcoming: event_date today-or-future AND status is pending or approved
  //   (approved counts here too because an attended event might still be "today").
  // Completed: event_date in the past OR status is approved/attended.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const eventDateOf = (ev) => {
    const d = ev.event_date || ev.start_date;
    return d ? new Date(d) : null;
  };
  const isUpcoming = (ev) => {
    const d = eventDateOf(ev);
    const status = ev.my_status || 'pending';
    if (!d) return ['pending', 'approved'].includes(status);
    return d >= today && ['pending', 'approved'].includes(status);
  };
  const isCompleted = (ev) => {
    const d = eventDateOf(ev);
    const status = ev.my_status || 'pending';
    if (status === 'approved' || status === 'attended') return true;
    return d ? d < today : false;
  };
  const filteredEvents = myEvents.filter(madSubFilter === 'active' ? isUpcoming : isCompleted);

  const getEventBadge = (ev) => {
    const status = ev.my_status || 'pending';
    const d = eventDateOf(ev);
    const isPast = d && d < today;
    if (status === 'approved' || status === 'attended') {
      return { label: 'Attended', color: '#047857', bg: '#D1FAE5' };
    }
    if (isPast && status === 'pending') {
      return { label: 'Verification Pending', color: '#9A3412', bg: '#FFEDD5' };
    }
    return { label: 'Upcoming', color: '#1D4ED8', bg: '#DBEAFE' };
  };
  const formatEventDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  return (
    <div className="page-container">
      <CommunityTopHeader />
      <div className="content-wrapper" style={{ background: '#F8FAFB' }}>
        <div className="content-section" style={{ padding: '0 16px' }}>

          {/* Profile Card */}
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 20, background: 'rgba(77,182,172,0.06)', padding: 12, marginBottom: 16 }}>
            <div style={{ position: 'relative', zIndex: 1, background: '#FFFFFF', borderRadius: 16, padding: '20px', border: '1.5px solid rgba(77,182,172,0.30)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: `linear-gradient(135deg, ${TEAL}, #3a9e8c)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, fontWeight: 700 }}>
                  {user.username?.charAt(0).toUpperCase() || 'C'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', marginBottom: 2 }}>{user.username}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>{user.email}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {getImpactBadge(user.impact_level || 'Bronze')}
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{user.impact_level || 'Bronze'}</span>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>|</span>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>{user.social_score || 0} pts</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Glance */}
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 16, background: 'rgba(242,140,40,0.06)', padding: 10, marginBottom: 16 }}>
            <div style={{ position: 'relative', zIndex: 1, background: '#FFFFFF', borderRadius: 12, padding: '14px 12px', border: '1.5px solid rgba(242,140,40,0.30)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Weekly Glance</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {weekDays.map((day, i) => (
                  <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: 10, color: day.isToday ? TEAL : '#9CA3AF', fontWeight: day.isToday ? 600 : 400, marginBottom: 6 }}>{day.dayName}</div>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', margin: '0 auto', background: day.isToday ? TEAL : 'transparent', border: `2px solid ${day.isToday ? TEAL : '#E5E7EB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: day.isToday ? '#fff' : '#374151', fontSize: 13, fontWeight: day.isToday ? 700 : 500 }}>
                      {day.dayNum}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#F1F3F5', borderRadius: 10, padding: 3 }}>
            {[
              { key: 'myevents', label: 'My Events', icon: Calendar },
              { key: 'myposts', label: 'My Posts', icon: Edit3 },
              { key: 'myactivity', label: 'Activity', icon: Clock }
            ].map(tab => (
              <button key={tab.key} data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                style={{ flex: 1, padding: '10px 4px', background: activeTab === tab.key ? 'white' : 'transparent', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, color: activeTab === tab.key ? '#1F2937' : '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.2s' }}>
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* My Events Tab */}
          {activeTab === 'myevents' && (
            <div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[
                  { key: 'active', label: 'Upcoming' },
                  { key: 'completed', label: 'Completed' },
                ].map(f => (
                  <button key={f.key} data-testid={`events-filter-${f.key}`}
                    onClick={() => setMadSubFilter(f.key)}
                    style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: `1px solid ${madSubFilter === f.key ? TEAL : '#E5E7EB'}`, background: madSubFilter === f.key ? TEAL_LIGHT : '#fff', color: madSubFilter === f.key ? TEAL : '#6B7280' }}>
                    {f.label}
                  </button>
                ))}
              </div>

              {filteredEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                  <Calendar size={32} color="#D1D5DB" style={{ marginBottom: 12 }} />
                  <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
                    {madSubFilter === 'active' ? "You haven't RSVP'd to any upcoming events" : 'No completed events yet'}
                  </p>
                  <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>
                    {madSubFilter === 'active' ? 'Find one on the WE feed and tap Join.' : 'Verified events will appear here.'}
                  </p>
                </div>
              ) : filteredEvents.map(ev => {
                const badge = getEventBadge(ev);
                const stars = ev.reward_stars || ev.reward_points || 30;
                return (
                  <div key={ev.id}
                    data-testid={`event-card-${ev.id}`}
                    onClick={() => navigate(`/community/event-detail/${ev.id}`)}
                    style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10, border: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: TEAL_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Calendar size={20} color={TEAL} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', flex: 1, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{ev.title}</div>
                        <span data-testid={`event-status-${ev.id}`}
                          style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, color: badge.color, background: badge.bg, whiteSpace: 'nowrap', letterSpacing: 0.2 }}>
                          {badge.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: 12, color: '#6B7280' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={12} />
                          {formatEventDate(ev.event_date || ev.start_date)}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Star size={12} fill="#F59E0B" color="#F59E0B" />{stars}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <UsersIcon size={12} />{ev.attendees_count ?? 0}
                        </span>
                        {(ev.location || ev.city) && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                            <MapPin size={12} />{ev.location || ev.city}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={18} color="#9CA3AF" style={{ alignSelf: 'center', flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          )}

          {/* My Posts Tab */}
          {activeTab === 'myposts' && (
            <div>
              {myPosts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                  <Edit3 size={32} color="#D1D5DB" style={{ marginBottom: 12 }} />
                  <p style={{ fontSize: 14, color: '#6B7280' }}>No posts yet</p>
                  <p style={{ fontSize: 12, color: '#9CA3AF' }}>Share something with the community!</p>
                </div>
              ) : myPosts.map(post => (
                <div key={post.id} style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 10, border: '1px solid #E5E7EB' }}>
                  <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.5, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content}</p>
                  {post.tags?.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                      {post.tags.map((t, i) => <span key={i} style={{ fontSize: 11, color: TEAL, fontWeight: 500 }}>#{t}</span>)}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#9CA3AF' }}>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Heart size={12} />{Array.isArray(post.likes) ? post.likes.length : post.likes || 0}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MessageSquare size={12} />{post.comments?.length || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* My Activity Tab */}
          {activeTab === 'myactivity' && (
            <div>
              {myActivity.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                  <Clock size={32} color="#D1D5DB" style={{ marginBottom: 12 }} />
                  <p style={{ fontSize: 14, color: '#6B7280' }}>No activity yet</p>
                  <p style={{ fontSize: 12, color: '#9CA3AF' }}>Start posting and joining challenges!</p>
                </div>
              ) : myActivity.map((act, i) => (
                <div key={act.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: i < myActivity.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #E5E7EB' }}>
                    {getActivityIcon(act.type)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>{act.description}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{act.created_at ? new Date(act.created_at).toLocaleDateString() : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Merged-in Impact sections (Your Impact / Your Stats / Leaderboard) ── */}
          <div style={{ marginTop: 24 }} data-testid="me-impact-sections">
            <CommunityImpactSections />
          </div>

          <div style={{ height: 24 }} />
        </div>
      </div>
      <CommunityBottomNav active="profile" />
    </div>
  );
}
